/**
 * One optimizer rule IN the Flow Optimizer's own rule card (user, 12/10/2026:
 * "use the same component as flow optimiser"): its header (click-to-edit
 * name, delete — here with the rule's on/off beside it), its sentence ("when
 * ALL (AND) / ANY (OR) of these match" — no layers here), its conditions list
 * (field, coloured operators, value, the connector between lines, + Condition
 * / + NOT — no score-ranking fields and no ⇄ compare: the Automation judges
 * plain thresholds), "measured over" the formula's first range (inherited) or
 * the rule's own, and its "↳ then" block with the one-action picker and what
 * the action does on this surface. Every surface's rules also pick its Tag
 * Rules tags in the same field picker (sections "Hour tag", "Text tag",
 * "Gateway tag", "List tag" — Phase 25), and say when a tag they test no
 * longer exists.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Info } from 'lucide-react';
import { DROP_GEN_RULE_LIMITS, type DropGenRule, usesRanking } from '@trafficpulse/shared';

import { Switch } from '@/components/ui';
import { cn } from '@/lib/utils';
import { RuleCardHeader, RuleConditionsList, RuleConsequenceFrame, RuleSentence } from '@/features/flow-optimizer/tabs/exclude/group';

import type { GroupTag } from '@/features/flow-optimizer/tabs/exclude/rule-condition/kind/condition-kind';

import { linesOf, newLine, whenOf, type RuleLines } from '../rule-model';
import { actionMeaning, RULE_ACTIONS, RULE_SURFACES, type RuleSurface } from '../rule-surfaces';
import { MissingTagNote, tagOptions, useRuleTagCatalog } from '../tags';
import { type FirstRange, MeasuredOver } from './MeasuredOver';
import { RuleActionPicker } from './RuleActionPicker';
import { RuleScopeRow, scopeMeaning } from './RuleScopeRow';

export type { FirstRange };

interface Props {
  rule: DropGenRule;
  surface: RuleSurface;
  /** 1-based, for the "Rule N" name fallback. */
  index: number;
  /** The formula's first range — what an inherited rule reads. */
  firstRange?: FirstRange;
  /** What it does to today's result, when worth a line — "it changes nothing today". */
  note?: string;
  onChange: (patch: Partial<DropGenRule>) => void;
  onRemove: () => void;
}

/** The "then" block's edge, the Flow Optimizer's way: Exclude blue, taking away red, favouring green. */
function edgeOf(action: string): string {
  const tone = action === 'exclude' ? null : RULE_ACTIONS[action]?.tone;
  if (tone === 'destructive') return 'border-l-destructive bg-destructive/5';
  if (tone === 'success') return 'border-l-success bg-success/5';
  return 'border-l-primary bg-primary/5';
}

export const RuleCard: FC<Props> = ({ rule, surface, index, firstRange, note, onChange, onRemove }) => {
  const lines = linesOf(rule.when);
  const { data: catalog } = useRuleTagCatalog(surface);
  return (
    <article className={cn('space-y-3 rounded-lg border border-border bg-card p-3.5 shadow-sm', !rule.enabled && 'opacity-70')} data-testid="rule-card">
      <RuleCardHeader
        name={rule.name}
        index={index}
        onRename={(name) => onChange({ name: name ?? `Rule ${index}` })}
        onRemove={onRemove}
        aside={<Switch checked={rule.enabled} onCheckedChange={(enabled) => onChange({ enabled })} aria-label={`Turn the rule ${rule.name} on or off`} />}
      />
      {lines ? (
        <RuleLinesEditor lines={lines} surface={surface} excludes={rule.action === 'exclude'} tags={tagOptions(catalog, RULE_SURFACES[surface].tags.group)} onLines={(next) => onChange({ when: whenOf(next) })} footer={<MeasuredOver rule={rule} surface={surface} firstRange={firstRange} onChange={onChange} />} />
      ) : (
        <p className="text-xs italic text-muted-foreground">This rule has nested conditions — kept exactly as saved.</p>
      )}
      <MissingTagNote rule={rule} catalog={catalog} surface={surface} />
      <RuleConsequenceFrame
        edgeClass={edgeOf(rule.action)}
        picker={<RuleActionPicker surface={surface} value={rule.action} onChange={(action) => onChange({ action, scope: action === 'block' ? rule.scope : undefined, reduceSlots: action === 'reduce_slots' ? rule.reduceSlots ?? { count: 1, minRemaining: 1 } : undefined })} blocked={usesRanking(rule.when) ? NO_EXCLUDE : undefined} />}
      >
        {surface === 'hours' && rule.action === 'reduce_slots' ? <div className="flex flex-wrap items-center gap-2 text-xs">
          Remove <input type="number" min={1} aria-label="Slots to remove" value={rule.reduceSlots?.count ?? 1} onChange={(e) => {
            const count = Number(e.target.value);
            if (Number.isSafeInteger(count) && count >= 1) onChange({ reduceSlots: { minRemaining: rule.reduceSlots?.minRemaining ?? 1, count } });
          }} className="w-16 rounded border border-border bg-background px-2 py-1" /> slots; keep at least
          <input type="number" min={0} aria-label="Minimum slots remaining" value={rule.reduceSlots?.minRemaining ?? 1} onChange={(e) => {
            const minRemaining = Number(e.target.value);
            if (Number.isSafeInteger(minRemaining) && minRemaining >= 0) onChange({ reduceSlots: { count: rule.reduceSlots?.count ?? 1, minRemaining } });
          }} className="w-16 rounded border border-border bg-background px-2 py-1" />
        </div> : null}
        {surface === 'hours' && rule.action === 'block'  ? <RuleScopeRow scope={rule.scope} onChange={(scope) => onChange({ scope })} /> : null}
        <p key={`${rule.action}:${rule.scope?.lists ?? 'all'}`} className="text-xs text-muted-foreground duration-300 animate-in fade-in-0 motion-reduce:animate-none">
          {actionMeaning(surface, rule.action)}
          {rule.action === 'block' && rule.scope ? ` ${scopeMeaning(rule.scope)}` : ''}
        </p>
        {note ? (
          <p className="mt-1.5 flex items-start gap-1.5 text-xs text-foreground/80 duration-300 animate-in fade-in-0 motion-reduce:animate-none">
            <Info className="mt-px h-3.5 w-3.5 shrink-0 text-warning" aria-hidden />
            {note}
          </p>
        ) : null}
      </RuleConsequenceFrame>
    </article>
  );
};

/** Exclude decides who is ranked, so a rule that reads the ranking cannot exclude. */
const NO_EXCLUDE = { exclude: 'Exclude decides what is ranked — remove the score ranking first' };

type LinesEditorProps = { lines: RuleLines; surface: RuleSurface; excludes: boolean; tags: GroupTag[]; onLines: (next: RuleLines) => void; footer: JSX.Element };

const RuleLinesEditor: FC<LinesEditorProps> = ({ lines, surface, excludes, tags, onLines, footer }) => {
  const add = (not: boolean) =>
    lines.items.length < DROP_GEN_RULE_LIMITS.conditions && onLines({ ...lines, items: [...lines.items, newLine(surface, not, lines.items.length)] });
  return (
    <>
      <RuleSentence op={lines.op} onOpChange={(op) => onLines({ ...lines, op })} />
      <RuleConditionsList
        items={lines.items}
        op={lines.op}
        layer="paths"
        tags={tags}
        rowOptions={{ rankingMetrics: excludes ? [] : ['rawScore'], allowCompare: false }}
        onReplace={(next) => onLines({ ...lines, items: lines.items.map((i) => (i.id === next.id ? next : i)) })}
        onRemove={(id) => onLines({ ...lines, items: lines.items.filter((i) => i.id !== id) })}
        onAddCondition={() => add(false)}
        onAddNot={() => add(true)}
        footer={footer}
      />
    </>
  );
};
